// RadicalScreen.jsx
import React, {
    useEffect,
    useMemo,
    useState,
    useRef,
    useCallback,
} from "react";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    FormGroup,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Snackbar,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    ToggleButton,
    ToggleButtonGroup,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    SpeedDial,
    SpeedDialAction,
    SpeedDialIcon,
    Avatar,
} from "@mui/material";
import {
    BUILT_IN_PRESETS,
    CARD_SUIT_ALIAS_GROUPS,
    Icons,
    PICTOGRAM_LAYOUT_PRESETS,
    STORAGE_KEY,
    clampPositiveInt,
    codeToNumeric,
    codeToPictogram,
    countFilledCards,
    createUserPresetId,
    generateRawCodes,
    getCardTextColor,
    getDisplayPosition,
    getFinalCode,
    getInverseInfo,
    getLetters,
    getPictogramsForPackets,
    getPositionTooltipText,
    getPresetLabel,
    getRelativePosition,
    getSchemeText,
    getVisibleCardsByCode,
    isPalindromeCode,
    loadInitialState,
    normalizeCardText,
    normalizeHandlingPreset,
    normalizeDeckOrientation,
    normalizeImportedPreset,
    normalizeUserPreset,
    presetMatchesCurrentRecipe,
    remapCardsByOppositeCode,
} from "../components/RadicalHelpers";
import {
    DialogExport,
    exportRadicalPresets,
} from "../components/RadicalExportHelpers";
import RadicalSimulation from "../components/RadicalSimulation";
import RadicalMoveDialog from "../components/RadicalMoveDialog";
import logoOrdenacionRadical from "../imgs/LogoOrdenacionRadical.svg";
import RadicalSymbolSwapDialog from "../components/RadicalSymbolSwapDialog";
import {
    GoogleIcon,
    LogoutIcon,
    useGoogleAuth,
} from "../components/Nodos/GoogleAuth";
import { createRadicalPresetSync } from "../helpers/RadicalPresetSync";
import RadicalAdvancedMode from "../components/RadicalAdvancedMode";
import {
    RadicalConfirmDialog,
    RadicalRenamePresetDialog,
    RadicalSavePresetDialog,
    RadicalUnsavedChangesDialog,
} from "../components/RadicalPresetDialogs";
import RadicalSearchDialog from "../components/RadicalSearchDialog";
import RadicalPictogramSelector from "../components/RadicalPictogramSelector";

const HISTORY_LIMIT = 50;

const CompactTable = React.memo(
    ({
        rows,
        allRows,
        cardsByCode,
        onCardChange,
        onCardMove,
        onCardKeyDown,
        cardInputRefs,
        packets,
        pictograms,
        deckOrientation,
        highlightedCodes,
    }) => {
        const totalPositions = allRows.length;
        const [dragOverCode, setDragOverCode] = useState("");

        return (
            <Box sx={{ width: "max-content" }}>
                <Table
                    size="small"
                    sx={{
                        tableLayout: "auto",
                        width: "auto",
                        borderCollapse: "separate",
                        borderSpacing: 0,
                        "& .MuiTableCell-root": {
                            py: 0.35,
                            px: 0.75,
                            fontSize: 12,
                            whiteSpace: "nowrap",
                            borderBottom: "1px solid",
                            borderColor: "divider",
                        },
                    }}
                >
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}>
                                <Tooltip
                                    arrow
                                    title={
                                        deckOrientation === "dorso"
                                            ? "Posición (dorso). La numeración está invertida respecto a cara."
                                            : "Posición (cara)"
                                    }
                                >
                                    #
                                </Tooltip>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>
                                <Tooltip
                                    arrow
                                    title="Posición relativa dentro de las cartas asignadas"
                                >
                                    #R
                                </Tooltip>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>
                                Código
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>
                                <Tooltip arrow title={"Código numérico"}>
                                    Num
                                </Tooltip>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>
                                Pictograma
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>
                                Carta
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>
                                <Tooltip
                                    arrow
                                    title={
                                        <Box sx={{ whiteSpace: "pre-line" }}>
                                            Absoluta / Relativa
                                            {"\n"}
                                            {"\n"}
                                            Absoluta: Posición teórica del
                                            código invertido.
                                            {"\n"}
                                            No tiene en cuenta huecos ni libera
                                            la posición original.
                                            {"\n"}
                                            {"\n"}
                                            Relativa: Posición real al mover
                                            solo esta carta.
                                            {"\n"}
                                            Aquí sí se libera su posición
                                            original y luego se compactan las
                                            posiciones ocupadas.
                                        </Box>
                                    }
                                >
                                    <Box component="span"># Inversa</Box>
                                </Tooltip>
                            </TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {rows.map((row) => {
                            const relativePosition = getRelativePosition(
                                row,
                                allRows,
                                cardsByCode,
                                deckOrientation,
                            );
                            const palindrome = isPalindromeCode(row.finalCode);
                            const numericCode = codeToNumeric(
                                row.finalCode,
                                packets,
                            );
                            const pictogramCode = codeToPictogram(
                                row.finalCode,
                                pictograms,
                            );
                            const cardValue = cardsByCode[row.finalCode] || "";
                            const cardTextColor = getCardTextColor(cardValue);
                            const displayPosition = getDisplayPosition(
                                row.position,
                                totalPositions,
                                deckOrientation,
                            );

                            const inverseInfo = getInverseInfo(
                                row,
                                allRows,
                                cardsByCode,
                                deckOrientation,
                            );

                            return (
                                <TableRow
                                    key={row.finalCode}
                                    onDragOver={(event) => {
                                        event.preventDefault();
                                        event.dataTransfer.dropEffect = "move";
                                        setDragOverCode(row.finalCode);
                                    }}
                                    onDragLeave={(event) => {
                                        if (
                                            !event.currentTarget.contains(
                                                event.relatedTarget,
                                            )
                                        ) {
                                            setDragOverCode("");
                                        }
                                    }}
                                    onDrop={(event) => {
                                        event.preventDefault();

                                        const sourceCode =
                                            event.dataTransfer.getData(
                                                "text/radical-card-code",
                                            );

                                        setDragOverCode("");

                                        if (sourceCode) {
                                            onCardMove(
                                                sourceCode,
                                                row.finalCode,
                                            );
                                        }
                                    }}
                                    sx={(theme) => ({
                                        bgcolor: highlightedCodes.includes(
                                            row.finalCode,
                                        )
                                            ? theme.palette.action.selected
                                            : "transparent",
                                        "& > td": {
                                            boxShadow:
                                                dragOverCode === row.finalCode
                                                    ? `inset 0 2px 0 ${theme.palette.primary.main}`
                                                    : "none",
                                        },
                                    })}
                                >
                                    <TableCell>
                                        <Tooltip
                                            title={getPositionTooltipText(
                                                row,
                                                packets,
                                                deckOrientation,
                                                totalPositions,
                                            )}
                                            arrow
                                            placement="top"
                                            slotProps={{
                                                tooltip: {
                                                    sx: {
                                                        whiteSpace: "pre-wrap",
                                                        fontSize: 12,
                                                    },
                                                },
                                            }}
                                        >
                                            <Box
                                                component="span"
                                                sx={{
                                                    cursor: "help",
                                                    display: "inline-block",
                                                }}
                                            >
                                                {displayPosition}
                                            </Box>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell>
                                        <Box
                                            component="span"
                                            sx={{
                                                fontFamily: "monospace",
                                                color: "text.secondary",
                                                fontSize: 11,
                                            }}
                                        >
                                            {relativePosition}
                                        </Box>
                                    </TableCell>

                                    <TableCell>
                                        <Box
                                            component="span"
                                            sx={{
                                                fontFamily: "monospace",
                                                color: palindrome
                                                    ? "error.main"
                                                    : "text.primary",
                                                fontWeight: palindrome
                                                    ? 700
                                                    : 400,
                                            }}
                                        >
                                            {row.finalCode}
                                        </Box>
                                    </TableCell>

                                    <TableCell>
                                        <Box
                                            component="span"
                                            sx={{
                                                fontFamily: "monospace",
                                                color: palindrome
                                                    ? "error.main"
                                                    : "text.secondary",
                                                fontWeight: palindrome
                                                    ? 700
                                                    : 400,
                                            }}
                                        >
                                            {numericCode}
                                        </Box>
                                    </TableCell>

                                    <TableCell>
                                        <Box
                                            component="span"
                                            sx={{
                                                color: palindrome
                                                    ? "error.main"
                                                    : "text.primary",
                                                fontWeight: palindrome
                                                    ? 700
                                                    : 400,
                                                letterSpacing: 0.5,
                                            }}
                                        >
                                            {pictogramCode}
                                        </Box>
                                    </TableCell>

                                    <TableCell>
                                        <Stack
                                            direction="row"
                                            spacing={0.5}
                                            alignItems="center"
                                        >
                                            <Box
                                                component="span"
                                                draggable
                                                onDragStart={(event) => {
                                                    event.dataTransfer.setData(
                                                        "text/radical-card-code",
                                                        row.finalCode,
                                                    );
                                                    event.dataTransfer.effectAllowed =
                                                        "move";
                                                }}
                                                onDragEnd={() => {
                                                    setDragOverCode("");
                                                }}
                                                sx={{
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    color: "text.disabled",
                                                    cursor: "grab",
                                                    "&:active": {
                                                        cursor: "grabbing",
                                                    },
                                                    "&:hover": {
                                                        color: "text.secondary",
                                                    },
                                                }}
                                            >
                                                <Icons.Drag
                                                    sx={{ fontSize: 16 }}
                                                />
                                            </Box>

                                            <TextField
                                                inputRef={(node) => {
                                                    if (node) {
                                                        cardInputRefs.current[
                                                            row.finalCode
                                                        ] = node;
                                                    } else {
                                                        delete cardInputRefs
                                                            .current[
                                                            row.finalCode
                                                        ];
                                                    }
                                                }}
                                                value={cardValue}
                                                onChange={(event) =>
                                                    onCardChange(
                                                        row.finalCode,
                                                        event.target.value,
                                                    )
                                                }
                                                onKeyDown={(event) =>
                                                    onCardKeyDown(
                                                        event,
                                                        row.finalCode,
                                                    )
                                                }
                                                placeholder="Carta"
                                                variant="standard"
                                                size="small"
                                                sx={{
                                                    width: 90,
                                                    minWidth: 90,
                                                    "& .MuiInputBase-input": {
                                                        py: 0.2,
                                                        px: 0,
                                                        fontSize: 12,
                                                        color: cardTextColor,
                                                        fontWeight: 500,
                                                        "&::placeholder": {
                                                            color: palindrome
                                                                ? "error.main"
                                                                : "text.disabled",
                                                            opacity: 0.75,
                                                        },
                                                    },
                                                }}
                                            />
                                        </Stack>
                                    </TableCell>

                                    <TableCell>
                                        <Tooltip
                                            arrow
                                            title={
                                                <Box
                                                    sx={{
                                                        whiteSpace: "pre-line",
                                                    }}
                                                >
                                                    <b>Absoluto:{"\n"}</b>
                                                    {
                                                        inverseInfo.absoluteTooltip
                                                    }
                                                    {"\n\n"}
                                                    <b>Relativo:{"\n"}</b>
                                                    {
                                                        inverseInfo.relativeTooltip
                                                    }
                                                </Box>
                                            }
                                        >
                                            <Box
                                                component="span"
                                                sx={{ fontFamily: "monospace" }}
                                            >
                                                {inverseInfo.inverseAbsolutePosition ??
                                                    ""}{" "}
                                                /{" "}
                                                {inverseInfo.inverseRelativePosition ??
                                                    ""}
                                            </Box>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </Box>
        );
    },
);

const TwoDealGrid = React.memo(
    ({
        rows,
        cardsByCode,
        onCardChange,
        cardInputRefs,
        packets,
        pictograms,
        deckOrientation,
        highlightedCodes,
    }) => {
        const letters = useMemo(() => getLetters(packets), [packets]);
        const secondLetters = useMemo(() => [...letters].reverse(), [letters]);
        const rowsByCode = useMemo(
            () => new Map(rows.map((row) => [row.finalCode, row])),
            [rows],
        );

        const handleKeyDown = useCallback(
            (event, rowIndex, columnIndex) => {
                if (
                    (event.key !== "ArrowUp" && event.key !== "ArrowDown") ||
                    event.altKey ||
                    event.ctrlKey ||
                    event.metaKey ||
                    event.shiftKey
                ) {
                    return;
                }

                const nextSecondLetter =
                    secondLetters[
                        rowIndex + (event.key === "ArrowDown" ? 1 : -1)
                    ];

                if (!nextSecondLetter) {
                    return;
                }

                event.preventDefault();

                const nextCode = `${letters[columnIndex]}${nextSecondLetter}`;

                window.requestAnimationFrame(() => {
                    cardInputRefs.current[nextCode]?.focus();
                    cardInputRefs.current[nextCode]?.select();
                });
            },
            [cardInputRefs, letters, secondLetters],
        );

        return (
            <Box sx={{ maxWidth: "100%", overflowX: "auto" }}>
                <Table
                    size="small"
                    sx={{
                        width: "max-content",
                        minWidth: "100%",
                        borderCollapse: "collapse",
                        "& .MuiTableCell-root": {
                            border: "1px solid",
                            borderColor: "divider",
                            p: 0.75,
                        },
                    }}
                >
                    <TableHead>
                        <TableRow>
                            <TableCell
                                sx={(theme) => ({
                                    position: "relative",
                                    width: 170,
                                    minWidth: 170,
                                    height: 64,
                                    p: 0,
                                    bgcolor: "background.paper",
                                    backgroundImage: `linear-gradient(
                                        to top right,
                                        transparent 49.5%,
                                        ${theme.palette.divider} 50%,
                                        transparent 50.5%
                                    )`,
                                })}
                            >
                                <Typography
                                    variant="caption"
                                    sx={{
                                        position: "absolute",
                                        top: 7,
                                        right: 9,
                                        fontWeight: 700,
                                        lineHeight: 1,
                                    }}
                                >
                                    Primera
                                </Typography>

                                <Typography
                                    variant="caption"
                                    sx={{
                                        position: "absolute",
                                        bottom: 7,
                                        left: 9,
                                        fontWeight: 700,
                                        lineHeight: 1,
                                    }}
                                >
                                    Segunda
                                </Typography>
                            </TableCell>

                            {letters.map((letter) => (
                                <TableCell
                                    key={letter}
                                    align="center"
                                    sx={{
                                        minWidth: 120,
                                        bgcolor: "background.paper",
                                    }}
                                >
                                    <Stack
                                        direction="row"
                                        spacing={0.5}
                                        alignItems="center"
                                        justifyContent="center"
                                    >
                                        <Typography
                                            component="span"
                                            variant="caption"
                                            sx={{ fontWeight: 700 }}
                                        >
                                            {letter}
                                        </Typography>

                                        <Typography
                                            component="span"
                                            sx={{ fontSize: 15, lineHeight: 1 }}
                                        >
                                            {pictograms[letter] || letter}
                                        </Typography>
                                    </Stack>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {secondLetters.map((secondLetter, rowIndex) => (
                            <TableRow key={secondLetter}>
                                <TableCell
                                    component="th"
                                    scope="row"
                                    align="center"
                                    sx={{ bgcolor: "background.paper" }}
                                >
                                    <Stack
                                        direction="row"
                                        spacing={0.5}
                                        alignItems="center"
                                        justifyContent="center"
                                    >
                                        <Typography
                                            component="span"
                                            variant="caption"
                                            sx={{ fontWeight: 700 }}
                                        >
                                            {secondLetter}
                                        </Typography>

                                        <Typography
                                            component="span"
                                            sx={{ fontSize: 15, lineHeight: 1 }}
                                        >
                                            {pictograms[secondLetter] ||
                                                secondLetter}
                                        </Typography>
                                    </Stack>
                                </TableCell>

                                {letters.map((firstLetter, columnIndex) => {
                                    const code = `${firstLetter}${secondLetter}`;
                                    const row = rowsByCode.get(code);

                                    if (!row) {
                                        return <TableCell key={code} />;
                                    }

                                    const cardValue = cardsByCode[code] || "";
                                    const palindrome = isPalindromeCode(code);
                                    const displayPosition = getDisplayPosition(
                                        row.position,
                                        rows.length,
                                        deckOrientation,
                                    );
                                    const positionLabel =
                                        displayPosition === 1
                                            ? `top ${deckOrientation}`
                                            : displayPosition === rows.length
                                              ? `bottom ${deckOrientation}`
                                              : "";
                                    const isEdgePosition =
                                        Boolean(positionLabel);

                                    return (
                                        <TableCell
                                            key={code}
                                            sx={(theme) => ({
                                                position: "relative",
                                                minWidth: 120,
                                                bgcolor:
                                                    highlightedCodes.includes(
                                                        code,
                                                    )
                                                        ? theme.palette.action
                                                              .selected
                                                        : "transparent",
                                            })}
                                        >
                                            {positionLabel && (
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        position: "absolute",
                                                        top: 3,
                                                        right: 4,
                                                        fontSize: 8,
                                                        lineHeight: 1,
                                                        color: "text.secondary",
                                                        pointerEvents: "none",
                                                        textTransform:
                                                            "lowercase",
                                                    }}
                                                >
                                                    {positionLabel}
                                                </Box>
                                            )}

                                            <Tooltip
                                                arrow
                                                placement="top"
                                                title={
                                                    <Box
                                                        sx={{
                                                            whiteSpace:
                                                                "pre-line",
                                                        }}
                                                    >
                                                        {`Código: ${code} (${codeToPictogram(
                                                            code,
                                                            pictograms,
                                                        )})\nPosición (${deckOrientation}): ${displayPosition} de ${rows.length}`}
                                                    </Box>
                                                }
                                            >
                                                <TextField
                                                    inputRef={(node) => {
                                                        if (node) {
                                                            cardInputRefs.current[
                                                                code
                                                            ] = node;
                                                        } else {
                                                            delete cardInputRefs
                                                                .current[code];
                                                        }
                                                    }}
                                                    value={cardValue}
                                                    onChange={(event) =>
                                                        onCardChange(
                                                            code,
                                                            event.target.value,
                                                        )
                                                    }
                                                    onKeyDown={(event) =>
                                                        handleKeyDown(
                                                            event,
                                                            rowIndex,
                                                            columnIndex,
                                                        )
                                                    }
                                                    placeholder="Carta"
                                                    variant="standard"
                                                    size="small"
                                                    sx={{
                                                        width: 105,
                                                        "& .MuiInputBase-input":
                                                            {
                                                                py: 0.2,
                                                                px: 0,
                                                                fontSize: 12,
                                                                color: getCardTextColor(
                                                                    cardValue,
                                                                ),
                                                                fontWeight:
                                                                    isEdgePosition
                                                                        ? 700
                                                                        : 500,
                                                                "&::placeholder":
                                                                    {
                                                                        color: palindrome
                                                                            ? "error.main"
                                                                            : "text.disabled",
                                                                        opacity: 0.75,
                                                                    },
                                                            },
                                                    }}
                                                />
                                            </Tooltip>
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Box>
        );
    },
);

const RadicalFocusView = ({
    displayRows,
    allRows,
    cardsByCode,
    packets,
    pictograms,
    deckOrientation,
    onClose,
}) => {
    const focusRows = useMemo(
        () =>
            displayRows.filter((row) =>
                String(cardsByCode[row.finalCode] || "").trim(),
            ),
        [cardsByCode, displayRows],
    );
    const [index, setIndex] = useState(0);

    useEffect(() => {
        setIndex((previous) =>
            Math.min(previous, Math.max(focusRows.length - 1, 0)),
        );
    }, [focusRows.length]);

    const changeIndex = useCallback(
        (step) =>
            setIndex((previous) =>
                Math.max(0, Math.min(focusRows.length - 1, previous + step)),
            ),
        [focusRows.length],
    );

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (
                event.altKey ||
                event.ctrlKey ||
                event.metaKey ||
                event.shiftKey ||
                (event.target?.closest?.(
                    'input, textarea, select, button, [contenteditable="true"], [role="dialog"], [role="listbox"], [role="menu"]',
                ) &&
                    !event.target?.closest?.("[data-radical-focus-view]"))
            ) {
                return;
            }

            if (event.key === "Escape") {
                event.preventDefault();
                onClose();
                return;
            }

            const step = ["ArrowLeft", "ArrowUp", "Backspace"].includes(
                event.key,
            )
                ? -1
                : ["ArrowRight", "ArrowDown", "Enter"].includes(event.key) ||
                    event.code === "Space"
                  ? 1
                  : 0;

            if (!step) {
                return;
            }

            event.preventDefault();
            changeIndex(step);
        };

        window.addEventListener("keydown", handleKeyDown, true);
        return () => window.removeEventListener("keydown", handleKeyDown, true);
    }, [changeIndex, onClose]);

    const currentRow = focusRows[index];

    if (!currentRow) {
        return null;
    }

    const inverseInfo = getInverseInfo(
        currentRow,
        allRows,
        cardsByCode,
        deckOrientation,
    );

    return (
        <Stack
            data-radical-focus-view=""
            spacing={{ xs: 2, md: 3 }}
            sx={{
                minHeight: { xs: "70dvh", md: "76dvh" },
                py: { xs: 2, md: 3 },
                alignItems: "center",
                justifyContent: "center",
            }}
        >
            <Typography variant="body2" color="text.secondary">
                {index + 1} / {focusRows.length}
            </Typography>

            <Box
                sx={{
                    width: "100%",
                    display: "grid",
                    gridTemplateColumns: "auto auto auto",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "clamp(8px, 3vw, 40px)",
                }}
            >
                {[-1, 0, 1].map((offset) => {
                    const row = focusRows[index + offset];
                    const secondary = offset !== 0;
                    const width = secondary
                        ? "clamp(44px, 9vw, 220px)"
                        : "min(64vw, 47dvh)";

                    if (!row) {
                        return (
                            <Box
                                key={offset}
                                aria-hidden
                                sx={{ width, aspectRatio: "5 / 7" }}
                            />
                        );
                    }

                    const cardValue = cardsByCode[row.finalCode] || "—";
                    const pictogram = codeToPictogram(
                        row.finalCode,
                        pictograms,
                    );

                    return (
                        <Paper
                            key={offset}
                            component={secondary ? "button" : "div"}
                            type={secondary ? "button" : undefined}
                            role={secondary ? undefined : "button"}
                            elevation={secondary ? 1 : 8}
                            aria-label={
                                secondary
                                    ? `Ir a la carta ${offset < 0 ? "anterior" : "siguiente"}: ${cardValue}`
                                    : `Carta actual: ${cardValue}`
                            }
                            onClick={(event) => {
                                if (secondary) {
                                    event.currentTarget.blur();
                                    changeIndex(offset);
                                    return;
                                }

                                const { left, width } =
                                    event.currentTarget.getBoundingClientRect();
                                changeIndex(
                                    event.clientX < left + width / 2 ? -1 : 1,
                                );
                            }}
                            sx={{
                                position: "relative",
                                width,
                                aspectRatio: "5 / 7",
                                p: 0,
                                overflow: "hidden",
                                border: secondary ? 1 : 2,
                                borderColor: secondary
                                    ? "divider"
                                    : "primary.main",
                                borderRadius: { xs: 2, md: 3 },
                                bgcolor: "background.paper",
                                color: "inherit",
                                font: "inherit",
                                cursor: "pointer",
                                opacity: secondary ? 0.62 : 1,
                                userSelect: "none",
                                transition:
                                    "opacity 120ms ease, transform 120ms ease, border-color 120ms ease",
                                "&:hover": secondary
                                    ? {
                                          opacity: 1,
                                          transform: "scale(1.04)",
                                          borderColor: "primary.main",
                                      }
                                    : undefined,
                                "&:focus-visible": {
                                    outline: "3px solid",
                                    outlineColor: "primary.main",
                                    outlineOffset: 3,
                                },
                            }}
                        >
                            <Stack
                                direction={secondary ? "column" : "row"}
                                divider={
                                    <Divider
                                        orientation={
                                            secondary
                                                ? "horizontal"
                                                : "vertical"
                                        }
                                        flexItem
                                    />
                                }
                                spacing={secondary ? 0.75 : 2.5}
                                sx={{
                                    position: "absolute",
                                    inset: secondary ? "12%" : "10% 7%",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <Typography
                                    sx={{
                                        color: getCardTextColor(cardValue),
                                        fontSize: secondary
                                            ? "clamp(20px, 3vw, 56px)"
                                            : "clamp(64px, 13vmin, 240px)",
                                        fontWeight: 900,
                                        lineHeight: 1,
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {cardValue}
                                </Typography>

                                <Typography
                                    sx={{
                                        fontSize: secondary
                                            ? "clamp(15px, 2.2vw, 42px)"
                                            : "clamp(38px, 7vmin, 140px)",
                                        fontWeight: 700,
                                        lineHeight: 1.15,
                                        letterSpacing: secondary ? 0 : 2,
                                        maxWidth: secondary ? "100%" : "48%",
                                        overflowWrap: "anywhere",
                                        textAlign: "center",
                                    }}
                                >
                                    {pictogram}
                                </Typography>
                            </Stack>
                        </Paper>
                    );
                })}
            </Box>

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    flexWrap: "wrap",
                    gap: { xs: 1.5, sm: 3 },
                }}
            >
                {[
                    [
                        "Posición",
                        getDisplayPosition(
                            currentRow.position,
                            allRows.length,
                            deckOrientation,
                        ),
                    ],
                    [
                        "Relativa",
                        getRelativePosition(
                            currentRow,
                            allRows,
                            cardsByCode,
                            deckOrientation,
                        ),
                    ],
                    ["Código", currentRow.finalCode],
                    ["Numérico", codeToNumeric(currentRow.finalCode, packets)],
                    [
                        "Inversa abs. / rel.",
                        `${inverseInfo.inverseAbsolutePosition ?? "—"} / ${
                            inverseInfo.inverseRelativePosition ?? "—"
                        }`,
                    ],
                ].map(([label, value]) => (
                    <Box key={label} sx={{ minWidth: 70, textAlign: "center" }}>
                        <Typography variant="caption" color="text.secondary">
                            {label}
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{ fontFamily: "monospace", fontWeight: 700 }}
                        >
                            {value}
                        </Typography>
                    </Box>
                ))}
            </Box>

            <Typography variant="caption" color="text.secondary">
                ← ↑ retroceder · → ↓ espacio enter avanzar · Esc salir
            </Typography>
        </Stack>
    );
};

const RadicalScreen = () => {
    const initialState = loadInitialState();
    const { session, busy, loginAllInOne, logout } = useGoogleAuth();
    const authUser = session?.user;

    const fileInputRef = useRef(null);
    const cardInputRefs = useRef({});

    const [cardsViewMode, setCardsViewMode] = useState("tables");
    const [isFocusMode, setIsFocusMode] = useState(false);

    const [deals, setDeals] = useState(initialState.deals);
    const [packets, setPackets] = useState(initialState.packets);
    const [deckOrientation, setDeckOrientation] = useState(
        normalizeDeckOrientation(initialState.deckOrientation),
    );
    const deckOrientationMeta = useMemo(() => {
        const isFace = deckOrientation === "cara";

        return {
            label: isFace ? "de cara" : "de dorso",
            shortLabel: isFace ? "cara" : "dorso",
            topLabel: isFace ? "Top (cara)" : "Top (dorso)",
            bottomLabel: isFace ? "Bottom (cara)" : "Bottom (dorso)",
            finalActionText: isFace
                ? "tras el final se volteará la baraja, dejándola de cara."
                : "tras el final no se volteará la baraja, dejándola de dorso.",
        };
    }, [deckOrientation]);
    const [cardsByCode, setCardsByCode] = useState(initialState.cardsByCode);
    const [pictogramsByPackets, setPictogramsByPackets] = useState(
        initialState.pictogramsByPackets,
    );
    const safeDeals = clampPositiveInt(deals, 1);
    const safePackets = clampPositiveInt(packets, 1);
    const [isCardAliasesDialogOpen, setIsCardAliasesDialogOpen] =
        useState(false);
    const [isRadicalMoveDialogOpen, setIsRadicalMoveDialogOpen] =
        useState(false);
    const [isSymbolSwapDialogOpen, setIsSymbolSwapDialogOpen] = useState(false);
    const [isAdvancedModeOpen, setIsAdvancedModeOpen] = useState(false);
    const [isSearchDialogOpen, setIsSearchDialogOpen] = useState(false);
    const [advancedSearchPresetId, setAdvancedSearchPresetId] = useState("");

    const playToolActions = [
        {
            name: "Modo avanzado",
            icon: "🧩",
            onClick: () => setIsAdvancedModeOpen(true),
        },
        {
            name: "Mover carta",
            icon: "🎯",
            onClick: () => setIsRadicalMoveDialogOpen(true),
        },
        {
            name: "Conectar símbolos",
            icon: "🔁",
            onClick: () => setIsSymbolSwapDialogOpen(true),
        },
        {
            name: "Buscar en recetas",
            icon: "🔎",
            onClick: () => setIsSearchDialogOpen(true),
        },
    ];

    const [description, setDescription] = useState(initialState.description);
    const [descriptionDraft, setDescriptionDraft] = useState(
        initialState.description,
    );
    const [descriptionViewMode, setDescriptionViewMode] = useState(
        initialState.descriptionViewMode || "compact",
    );

    const rows = useMemo(() => {
        const rawCodes = generateRawCodes(safePackets, safeDeals);

        return rawCodes.map((rawCode, index) => ({
            position: index + 1,
            rawCode,
            finalCode: getFinalCode(rawCode, safePackets),
        }));
    }, [safeDeals, safePackets]);

    const [userPresets, setUserPresets] = useState(initialState.userPresets);
    const userPresetsRef = useRef(initialState.userPresets);
    const presetSyncRef = useRef(null);

    const updateUserPresets = useCallback((value) => {
        const next =
            typeof value === "function" ? value(userPresetsRef.current) : value;

        userPresetsRef.current = next;
        setUserPresets(next);

        return next;
    }, []);
    const [selectedPresetId, setSelectedPresetId] = useState(
        initialState.selectedPresetId,
    );
    const normalUserPresets = useMemo(
        () => userPresets.filter((preset) => preset.recipeType !== "advanced"),
        [userPresets],
    );

    const advancedUserPresets = useMemo(
        () => userPresets.filter((preset) => preset.recipeType === "advanced"),
        [userPresets],
    );

    const allPresets = useMemo(
        () => [...BUILT_IN_PRESETS, ...normalUserPresets],
        [normalUserPresets],
    );

    const selectedPreset = allPresets.find(
        (item) => item.id === selectedPresetId,
    );

    const currentVisibleCards = useMemo(() => {
        return getVisibleCardsByCode(rows, cardsByCode);
    }, [rows, cardsByCode]);

    const isSelectedPresetModified = Boolean(
        selectedPreset &&
        !presetMatchesCurrentRecipe({
            preset: selectedPreset,
            safeDeals,
            safePackets,
            deckOrientation,
            currentVisibleCards,
            descriptionDraft,
        }),
    );

    useEffect(() => {
        if (deals === "" || packets === "") {
            if (selectedPresetId !== "custom") {
                setSelectedPresetId("custom");
            }

            return;
        }

        // Keep the preset selected while its dimensions remain unchanged.
        if (
            selectedPreset &&
            clampPositiveInt(selectedPreset.deals, 1) === safeDeals &&
            clampPositiveInt(selectedPreset.packets, 1) === safePackets
        ) {
            return;
        }

        const matchingPreset = allPresets.find((preset) =>
            presetMatchesCurrentRecipe({
                preset,
                safeDeals,
                safePackets,
                deckOrientation,
                currentVisibleCards,
                descriptionDraft,
            }),
        );
        const nextPresetId = matchingPreset?.id || "custom";

        if (selectedPresetId !== nextPresetId) {
            setSelectedPresetId(nextPresetId);
        }
    }, [
        allPresets,
        currentVisibleCards,
        deckOrientation,
        descriptionDraft,
        deals,
        packets,
        safeDeals,
        safePackets,
        selectedPreset,
        selectedPresetId,
    ]);

    const schemeText = useMemo(
        () => getSchemeText(safePackets, safeDeals),
        [safePackets, safeDeals],
    );
    const nonPalindromicCount = useMemo(() => {
        return rows.filter((row) => !isPalindromeCode(row.finalCode)).length;
    }, [rows]);

    const currentPictograms = useMemo(() => {
        return getPictogramsForPackets(safePackets, pictogramsByPackets);
    }, [safePackets, pictogramsByPackets]);

    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            localStorage.setItem(
                STORAGE_KEY,
                JSON.stringify({
                    deals: safeDeals,
                    packets: safePackets,
                    deckOrientation,
                    cardsByCode,
                    pictogramsByPackets,
                    userPresets,
                    selectedPresetId,
                    description,
                    descriptionViewMode,
                }),
            );
        }, 200);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [
        safeDeals,
        safePackets,
        deckOrientation,
        cardsByCode,
        pictogramsByPackets,
        userPresets,
        selectedPresetId,
        description,
        descriptionViewMode,
    ]);

    const [feedback, setFeedback] = useState({
        open: false,
        severity: "success",
        message: "",
    });
    const [confirmationDialog, setConfirmationDialog] = useState("");
    const [pendingPresetId, setPendingPresetId] = useState("");
    const [isRenamePresetDialogOpen, setIsRenamePresetDialogOpen] =
        useState(false);
    const [presetNameDraft, setPresetNameDraft] = useState("");

    const undoHistoryRef = useRef([]);
    const redoHistoryRef = useRef([]);
    const historyGroupRef = useRef("");
    const historyTimeoutRef = useRef(null);
    const [, setHistoryRevision] = useState(0);
    const [isSavePresetDialogOpen, setIsSavePresetDialogOpen] = useState(false);

    const [isExporting, setIsExporting] = useState(false);
    const [isImporting, setIsImporting] = useState(false);
    const [isTableExporting, setIsTableExporting] = useState(false);
    const [isTableExportDialogOpen, setIsTableExportDialogOpen] =
        useState(false);

    const handleOpenTableExportDialog = () => {
        setIsTableExportDialogOpen(true);
    };
    const handleCloseTableExportDialog = () => {
        if (isTableExporting) {
            return;
        }

        setIsTableExportDialogOpen(false);
    };

    const [highlightedCodes, setHighlightedCodes] = useState([]);

    const handleSimulationTableHighlightChange = useCallback((cards) => {
        setHighlightedCodes([
            ...new Set(
                cards
                    .flatMap((card) => [card.code, card.dealingCode])
                    .filter(Boolean),
            ),
        ]);
    }, []);

    const showFeedback = (message, severity = "success") => {
        setFeedback({
            open: true,
            severity,
            message,
        });
    };

    useEffect(() => {
        const handleSyncError = (error) => {
            console.error("Radical preset sync failed:", error);

            setFeedback({
                open: true,
                severity: "error",
                message: "No se pudieron sincronizar los presets.",
            });
        };

        const sync = createRadicalPresetSync({
            getPresets: () => userPresetsRef.current,

            onPresetsChange: (presets) => {
                userPresetsRef.current = presets;
                setUserPresets(presets);

                setSelectedPresetId((presetId) =>
                    presetId === "custom" ||
                    BUILT_IN_PRESETS.some((preset) => preset.id === presetId) ||
                    presets.some((preset) => preset.id === presetId)
                        ? presetId
                        : "custom",
                );
            },

            onError: handleSyncError,

            onSynced: ({ conflicts }) => {
                if (!conflicts) {
                    return;
                }

                setFeedback({
                    open: true,
                    severity: "warning",
                    message:
                        conflicts === 1
                            ? "Se ha creado un preset adicional por un conflicto de sincronización."
                            : `Se han creado ${conflicts} presets adicionales por conflictos de sincronización.`,
                });
            },
        });

        presetSyncRef.current = sync;
        sync.start().catch(handleSyncError);

        return () => {
            sync.stop();

            if (presetSyncRef.current === sync) {
                presetSyncRef.current = null;
            }
        };
    }, []);

    const handleCloseFeedback = (_, reason) => {
        if (reason === "clickaway") {
            return;
        }

        setFeedback((prev) => ({
            ...prev,
            open: false,
        }));
    };

    const getHistorySnapshot = useCallback(
        () => ({
            deals,
            packets,
            deckOrientation,
            cardsByCode: { ...cardsByCode },
            pictogramsByPackets: Object.fromEntries(
                Object.entries(pictogramsByPackets).map(([key, value]) => [
                    key,
                    { ...value },
                ]),
            ),
            description,
            descriptionDraft,
            selectedPresetId,
        }),
        [
            cardsByCode,
            deals,
            deckOrientation,
            description,
            descriptionDraft,
            packets,
            pictogramsByPackets,
            selectedPresetId,
        ],
    );

    const clearHistoryGroup = useCallback(() => {
        window.clearTimeout(historyTimeoutRef.current);
        historyTimeoutRef.current = null;
        historyGroupRef.current = "";
    }, []);

    const pushHistory = useCallback(
        (group = "") => {
            // Group consecutive text changes into a single history entry.
            if (group && historyGroupRef.current === group) {
                window.clearTimeout(historyTimeoutRef.current);
                historyTimeoutRef.current = window.setTimeout(() => {
                    historyGroupRef.current = "";
                }, 600);
                return;
            }

            undoHistoryRef.current = [
                ...undoHistoryRef.current.slice(-(HISTORY_LIMIT - 1)),
                getHistorySnapshot(),
            ];
            redoHistoryRef.current = [];
            setHistoryRevision((prev) => prev + 1);

            clearHistoryGroup();
            historyGroupRef.current = group;

            if (group) {
                historyTimeoutRef.current = window.setTimeout(() => {
                    historyGroupRef.current = "";
                }, 600);
            }
        },
        [clearHistoryGroup, getHistorySnapshot],
    );

    const clearHistory = useCallback(() => {
        undoHistoryRef.current = [];
        redoHistoryRef.current = [];
        clearHistoryGroup();
        setHistoryRevision((prev) => prev + 1);
    }, [clearHistoryGroup]);

    const restoreHistorySnapshot = useCallback((snapshot) => {
        setDeals(snapshot.deals);
        setPackets(snapshot.packets);
        setDeckOrientation(snapshot.deckOrientation);
        setCardsByCode({ ...snapshot.cardsByCode });
        setPictogramsByPackets(
            Object.fromEntries(
                Object.entries(snapshot.pictogramsByPackets).map(
                    ([key, value]) => [key, { ...value }],
                ),
            ),
        );
        setDescription(snapshot.description);
        setDescriptionDraft(snapshot.descriptionDraft);
        setSelectedPresetId(snapshot.selectedPresetId);
    }, []);

    const handleUndo = useCallback(() => {
        const snapshot = undoHistoryRef.current.pop();

        if (!snapshot) {
            return false;
        }

        redoHistoryRef.current = [
            ...redoHistoryRef.current.slice(-(HISTORY_LIMIT - 1)),
            getHistorySnapshot(),
        ];

        clearHistoryGroup();
        restoreHistorySnapshot(snapshot);
        setHistoryRevision((prev) => prev + 1);

        return true;
    }, [clearHistoryGroup, getHistorySnapshot, restoreHistorySnapshot]);

    const handleRedo = useCallback(() => {
        const snapshot = redoHistoryRef.current.pop();

        if (!snapshot) {
            return false;
        }

        undoHistoryRef.current = [
            ...undoHistoryRef.current.slice(-(HISTORY_LIMIT - 1)),
            getHistorySnapshot(),
        ];

        clearHistoryGroup();
        restoreHistorySnapshot(snapshot);
        setHistoryRevision((prev) => prev + 1);

        return true;
    }, [clearHistoryGroup, getHistorySnapshot, restoreHistorySnapshot]);

    useEffect(() => {
        const handleHistoryKeyDown = (event) => {
            if (
                (!event.ctrlKey && !event.metaKey) ||
                event.altKey ||
                event.target?.closest?.('[role="dialog"]')
            ) {
                return;
            }

            const key = event.key.toLowerCase();
            const handled =
                key === "z" && !event.shiftKey
                    ? handleUndo()
                    : key === "y" || (key === "z" && event.shiftKey)
                      ? handleRedo()
                      : false;

            if (handled) {
                event.preventDefault();
            }
        };

        window.addEventListener("keydown", handleHistoryKeyDown);

        return () => {
            window.removeEventListener("keydown", handleHistoryKeyDown);
        };
    }, [handleRedo, handleUndo]);

    useEffect(() => {
        return () => clearHistoryGroup();
    }, [clearHistoryGroup]);

    const handleDeckOrientationChange = (_, value) => {
        if (!value) {
            return;
        }

        const nextOrientation = normalizeDeckOrientation(value);

        if (nextOrientation === deckOrientation) {
            return;
        }

        pushHistory();

        setCardsByCode((prev) =>
            remapCardsByOppositeCode(prev, rows, safePackets),
        );
        setDeckOrientation(nextOrientation);
    };

    const handleOpenRenamePresetDialog = () => {
        if (!selectedPreset || selectedPreset.isBuiltIn) {
            return;
        }

        setPresetNameDraft(selectedPreset.name);
        setIsRenamePresetDialogOpen(true);
    };

    const handleRenamePreset = () => {
        const name = presetNameDraft.trim();

        if (!name || !selectedPreset || selectedPreset.isBuiltIn) {
            return;
        }

        const presetId = selectedPreset.id;

        updateUserPresets((prev) =>
            prev.map((preset) =>
                preset.id === presetId ? { ...preset, name } : preset,
            ),
        );

        presetSyncRef.current?.presetChanged(presetId);

        setIsRenamePresetDialogOpen(false);
        showFeedback(`Preset “${name}” renombrado.`);
    };

    const loadPreset = useCallback(
        (presetId) => {
            if (presetId === "custom") {
                setSelectedPresetId("custom");
                return;
            }

            const preset = allPresets.find((item) => item.id === presetId);

            if (!preset) {
                return;
            }

            const nextDescription =
                typeof preset.description === "string"
                    ? preset.description
                    : "";

            setDeals(preset.deals);
            setPackets(preset.packets);
            setDeckOrientation(
                normalizeDeckOrientation(preset.deckOrientation),
            );
            setCardsByCode({ ...(preset.cardsByCode || {}) });
            setDescription(nextDescription);
            setDescriptionDraft(nextDescription);
            setSelectedPresetId(preset.id);
            clearHistory();
        },
        [allPresets, clearHistory],
    );

    const handlePresetSelectChange = (event) => {
        const presetId = event.target.value;

        if (presetId === selectedPresetId) {
            return;
        }

        // Selecting Custom does not replace the current recipe.
        if (presetId !== "custom" && isSelectedPresetModified) {
            setPendingPresetId(presetId);
            return;
        }

        loadPreset(presetId);
    };

    const handleSavePreset = (overwrite = false) => {
        if (overwrite && selectedPreset && !selectedPreset.isBuiltIn) {
            const presetId = selectedPreset.id;

            updateUserPresets((prev) =>
                prev.map((preset) =>
                    preset.id === presetId
                        ? {
                              ...preset,
                              recipeType: "normal",
                              schemaVersion: 1,
                              description: descriptionDraft,
                              deals: safeDeals,
                              packets: safePackets,
                              deckOrientation,
                              cardsByCode: { ...currentVisibleCards },
                          }
                        : preset,
                ),
            );

            presetSyncRef.current?.presetChanged(presetId);

            setDescription(descriptionDraft);
            setIsSavePresetDialogOpen(false);
            showFeedback(`Preset “${selectedPreset.name}” actualizado.`);
            return;
        }

        const name = window.prompt(
            "Nombre del preset",
            selectedPreset?.name || "",
        );

        if (!name || !name.trim()) {
            return;
        }

        const nextPreset = {
            id: createUserPresetId(),
            name: name.trim(),
            description: descriptionDraft,
            recipeType: "normal",
            schemaVersion: 1,
            deals: safeDeals,
            packets: safePackets,
            deckOrientation,
            cardsByCode: { ...currentVisibleCards },
            isBuiltIn: false,
        };

        updateUserPresets((prev) => [...prev, nextPreset]);
        presetSyncRef.current?.presetChanged(nextPreset.id);
        setSelectedPresetId(nextPreset.id);
        setDescription(descriptionDraft);
        setIsSavePresetDialogOpen(false);
        showFeedback(`Preset “${nextPreset.name}” creado.`);
    };

    const handleSaveAdvancedPreset = (preset) => {
        const nextPreset = normalizeUserPreset(preset);

        if (!nextPreset) {
            return;
        }

        const wasExisting = userPresetsRef.current.some(
            (item) => item.id === nextPreset.id,
        );

        updateUserPresets((prev) =>
            wasExisting
                ? prev.map((item) =>
                      item.id === nextPreset.id ? nextPreset : item,
                  )
                : [...prev, nextPreset],
        );

        presetSyncRef.current?.presetChanged(nextPreset.id);

        showFeedback(
            `Receta avanzada “${nextPreset.name}” ${
                wasExisting ? "actualizada" : "creada"
            }.`,
        );
    };

    const handleDeleteAdvancedPreset = (presetId) => {
        const preset = userPresetsRef.current.find(
            (item) => item.id === presetId && item.recipeType === "advanced",
        );

        if (!preset) {
            return;
        }

        updateUserPresets((prev) =>
            prev.filter((item) => item.id !== presetId),
        );

        presetSyncRef.current?.presetDeleted(presetId);
        showFeedback(`Receta avanzada “${preset.name}” borrada.`);
    };

    const handleRenameAdvancedPreset = (presetId, name) => {
        const nextName = name.trim();

        if (!nextName) {
            return;
        }

        updateUserPresets((prev) =>
            prev.map((preset) =>
                preset.id === presetId
                    ? {
                          ...preset,
                          name: nextName,
                      }
                    : preset,
            ),
        );

        presetSyncRef.current?.presetChanged(presetId);
        showFeedback(`Receta avanzada “${nextName}” renombrada.`);
    };

    const handleDescriptionChange = (event) => {
        pushHistory("description");
        setDescriptionDraft(event.target.value);
    };

    const commitDescription = useCallback(() => {
        if (descriptionDraft !== description) {
            setDescription(descriptionDraft);
        }
    }, [description, descriptionDraft]);

    const handleDescriptionViewModeChange = (_, value) => {
        if (!value) {
            return;
        }

        setDescriptionViewMode(value);
    };

    const handleDeletePreset = () => {
        if (selectedPresetId === "custom") {
            return;
        }

        const selectedPreset = allPresets.find(
            (item) => item.id === selectedPresetId,
        );

        if (!selectedPreset || selectedPreset.isBuiltIn) {
            return;
        }

        const presetId = selectedPreset.id;

        updateUserPresets((prev) =>
            prev.filter((item) => item.id !== presetId),
        );

        setSelectedPresetId("custom");
        presetSyncRef.current?.presetDeleted(presetId);
    };

    const handleExportPresets = async () => {
        await exportRadicalPresets({
            builtInPresets: BUILT_IN_PRESETS,
            userPresets,
            showFeedback,
            onExportingChange: setIsExporting,
        });
    };

    const handleOpenImportDialog = () => {
        fileInputRef.current?.click();
    };

    const handleImportPresets = async (event) => {
        const file = event.target.files?.[0];

        event.target.value = "";

        if (!file) {
            return;
        }

        try {
            const text = await file.text();
            const parsed = JSON.parse(text);

            const rawPresets = Array.isArray(parsed)
                ? parsed
                : Array.isArray(parsed.presets)
                  ? parsed.presets
                  : [];

            if (!rawPresets.length) {
                showFeedback(
                    "El archivo no contiene presets válidos para importar.",
                    "warning",
                );
                return;
            }

            const importedPresets = [];
            let invalidCount = 0;
            let builtInCount = 0;

            rawPresets.forEach((preset) => {
                const normalizedPreset = normalizeImportedPreset(preset);

                if (!normalizedPreset) {
                    invalidCount += 1;
                    return;
                }

                if (normalizedPreset.isBuiltIn) {
                    builtInCount += 1;
                    return;
                }

                importedPresets.push({
                    ...normalizedPreset,
                    isBuiltIn: false,
                });
            });

            if (!importedPresets.length) {
                if (invalidCount > 0 || builtInCount > 0) {
                    const parts = [];

                    if (invalidCount > 0) {
                        parts.push(
                            `${invalidCount} inválido${invalidCount > 1 ? "s" : ""}`,
                        );
                    }

                    if (builtInCount > 0) {
                        parts.push(
                            `${builtInCount} built-in ignorado${builtInCount > 1 ? "s" : ""}`,
                        );
                    }

                    showFeedback(
                        `No se importó ningún preset. ${parts.join(" · ")}.`,
                        "error",
                    );
                    return;
                }

                showFeedback("No se importó ningún preset.", "error");
                return;
            }

            updateUserPresets((prev) => {
                const nextMap = new Map();

                prev.map(normalizeUserPreset)
                    .filter(Boolean)
                    .forEach((preset) => {
                        nextMap.set(preset.id, preset);
                    });

                importedPresets.forEach((preset) => {
                    nextMap.set(preset.id, preset);
                });

                return Array.from(nextMap.values());
            });

            presetSyncRef.current?.presetsChanged(
                importedPresets.map((preset) => preset.id),
            );

            if (invalidCount > 0 || builtInCount > 0) {
                const parts = [
                    `${importedPresets.length} importado${importedPresets.length > 1 ? "s" : ""}`,
                ];

                if (invalidCount > 0) {
                    parts.push(
                        `${invalidCount} inválido${invalidCount > 1 ? "s" : ""}`,
                    );
                }

                if (builtInCount > 0) {
                    parts.push(
                        `${builtInCount} built-in ignorado${builtInCount > 1 ? "s" : ""}`,
                    );
                }

                showFeedback(parts.join(" · "), "warning");
                return;
            }

            showFeedback(
                `${importedPresets.length} preset${importedPresets.length > 1 ? "s importados" : " importado"} correctamente.`,
                "success",
            );
        } catch (error) {
            showFeedback(
                "No se pudo leer el archivo. Revisa que sea un JSON válido.",
                "error",
            );
        }
    };

    const handleHandlingPresetsChange = (nextHandlingPresets) => {
        const normalizedHandlingPresets = nextHandlingPresets
            .map((handlingPreset) =>
                normalizeHandlingPreset(handlingPreset, safeDeals, safePackets),
            )
            .filter(Boolean);

        if (selectedPreset && !selectedPreset.isBuiltIn) {
            const presetId = selectedPreset.id;

            updateUserPresets((prev) =>
                prev.map((preset) =>
                    preset.id === presetId
                        ? {
                              ...preset,
                              handlingPresets: normalizedHandlingPresets,
                          }
                        : preset,
                ),
            );

            presetSyncRef.current?.presetChanged(presetId);

            showFeedback("Versiones de manejo guardadas.", "success");
            return;
        }

        const nextPreset = {
            id: createUserPresetId(),
            name: selectedPreset?.name || "Custom",
            description: descriptionDraft,
            deals: safeDeals,
            packets: safePackets,
            deckOrientation,
            cardsByCode: { ...currentVisibleCards },
            handlingPresets: normalizedHandlingPresets,
            isBuiltIn: false,
        };

        updateUserPresets((prev) => [nextPreset, ...prev]);
        presetSyncRef.current?.presetChanged(nextPreset.id);
        setSelectedPresetId(nextPreset.id);
        showFeedback("Receta guardada con sus versiones de manejo.", "success");
    };

    const canDeletePreset =
        selectedPresetId !== "custom" &&
        selectedPreset &&
        !selectedPreset.isBuiltIn;

    const handlePictogramChange = useCallback(
        (letter, value) => {
            pushHistory(`pictogram:${safePackets}:${letter}`);

            setPictogramsByPackets((prev) => ({
                ...prev,
                [safePackets]: {
                    ...getPictogramsForPackets(safePackets, prev),
                    [letter]: value,
                },
            }));
        },
        [pushHistory, safePackets],
    );

    const handlePictogramLayoutSelect = useCallback(
        (layout) => {
            pushHistory();

            setPictogramsByPackets((prev) => ({
                ...prev,
                [safePackets]: {
                    ...getPictogramsForPackets(safePackets, prev),
                    ...layout.pictograms,
                },
            }));
        },
        [pushHistory, safePackets],
    );

    const handleDealsBlur = () => {
        setDeals(clampPositiveInt(deals, 1));
    };

    const handlePacketsBlur = () => {
        setPackets(clampPositiveInt(packets, 1));
    };

    const handleDealsChange = (event) => {
        const value = event.target.value;

        pushHistory("deals");

        if (value === "") {
            setDeals("");
            setSelectedPresetId("custom");
            return;
        }

        setDeals(clampPositiveInt(value, 1));
        setSelectedPresetId("custom");
    };

    const handlePacketsChange = (event) => {
        const value = event.target.value;

        pushHistory("packets");

        if (value === "") {
            setPackets("");
            setSelectedPresetId("custom");
            return;
        }

        setPackets(clampPositiveInt(value, 1));
        setSelectedPresetId("custom");
    };

    const handleCardChange = useCallback(
        (code, value) => {
            pushHistory(`card:${code}`);

            setCardsByCode((prev) => ({
                ...prev,
                [code]: normalizeCardText(value),
            }));
        },
        [pushHistory],
    );

    const showWarning = safePackets > 26;

    const hasCurrentVisibleCards = Object.keys(currentVisibleCards).length > 0;
    const handleClearCurrentCards = () => {
        pushHistory();
        setCardsByCode((prev) => {
            const next = { ...prev };

            rows.forEach((row) => {
                delete next[row.finalCode];
            });

            return next;
        });

        setSelectedPresetId("custom");
    };
    const handleFlipCurrentCards = () => {
        pushHistory();
        setCardsByCode((prev) => {
            const next = { ...prev };
            const values = rows.map((row) => prev[row.finalCode] || "");

            rows.forEach((row, index) => {
                const value = values[values.length - 1 - index];

                if (String(value || "").trim()) {
                    next[row.finalCode] = value;
                } else {
                    delete next[row.finalCode];
                }
            });

            return next;
        });
    };

    const handleFlipFilledCards = () => {
        pushHistory();
        setCardsByCode((prev) => {
            const next = { ...prev };
            const filledRows = rows.filter((row) =>
                String(prev[row.finalCode] || "").trim(),
            );
            const values = filledRows
                .map((row) => prev[row.finalCode])
                .reverse();

            filledRows.forEach((row, index) => {
                next[row.finalCode] = values[index];
            });

            return next;
        });
    };

    const displayRows = useMemo(() => {
        return deckOrientation === "dorso" ? [...rows].reverse() : rows;
    }, [rows, deckOrientation]);

    const handleCardMove = useCallback(
        (sourceCode, targetCode) => {
            if (sourceCode === targetCode) {
                return;
            }
            pushHistory();

            setCardsByCode((prev) => {
                const displayCodes = displayRows.map((row) => row.finalCode);
                const sourceIndex = displayCodes.indexOf(sourceCode);
                const targetIndex = displayCodes.indexOf(targetCode);

                if (sourceIndex === -1 || targetIndex === -1) {
                    return prev;
                }

                const values = displayCodes.map((code) => prev[code] || "");
                const [movingValue] = values.splice(sourceIndex, 1);
                const insertIndex =
                    sourceIndex < targetIndex ? targetIndex - 1 : targetIndex;

                values.splice(insertIndex, 0, movingValue);

                const next = { ...prev };

                displayCodes.forEach((code, index) => {
                    const value = values[index];

                    if (String(value || "").trim()) {
                        next[code] = value;
                    } else {
                        delete next[code];
                    }
                });

                return next;
            });
        },
        [displayRows, pushHistory],
    );

    const handleCardKeyDown = useCallback(
        (event, code) => {
            if (event.key !== "ArrowUp" && event.key !== "ArrowDown") {
                return;
            }

            if (
                event.altKey ||
                event.ctrlKey ||
                event.metaKey ||
                event.shiftKey
            ) {
                return;
            }

            event.preventDefault();

            const currentIndex = displayRows.findIndex(
                (row) => row.finalCode === code,
            );
            const nextRow =
                displayRows[
                    currentIndex + (event.key === "ArrowDown" ? 1 : -1)
                ];

            if (!nextRow) {
                return;
            }

            window.requestAnimationFrame(() => {
                cardInputRefs.current[nextRow.finalCode]?.focus();
                cardInputRefs.current[nextRow.finalCode]?.select();
            });
        },
        [displayRows],
    );

    const { leftRows, rightRows } = useMemo(() => {
        const splitIndex = Math.ceil(displayRows.length / 2);

        return {
            leftRows: displayRows.slice(0, splitIndex),
            rightRows: displayRows.slice(splitIndex),
        };
    }, [displayRows]);

    const canRenamePreset = Boolean(
        selectedPreset && !selectedPreset.isBuiltIn,
    );
    const canUndo = undoHistoryRef.current.length > 0;
    const canRedo = redoHistoryRef.current.length > 0;

    return (
        <Box sx={{ p: 2 }}>
            <Stack spacing={2}>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="application/json,.json"
                    style={{ display: "none" }}
                    onChange={handleImportPresets}
                />
                <Box>
                    {/* <Typography variant="h6">Ordenación Radical</Typography> */}
                    <Box
                        component="img"
                        src={logoOrdenacionRadical}
                        alt="CJE"
                        sx={{
                            // height: 160,
                            maxWidth: "100%",
                            maxHeight: "160px",
                            width: "auto",
                            mr: 1.5,
                            display: "block",
                        }}
                    />
                    <Typography variant="body2" color="text.secondary">
                        Herramienta para generar y consultar la{" "}
                        <i>Ordenación Radical</i> según la cantidad de dadas y
                        paquetes, con su código final, equivalente numérico,
                        pictogramas y cartas asociadas.
                    </Typography>
                </Box>
                <Divider />

                <Accordion
                    disableGutters
                    sx={{
                        borderRadius: 1.5,
                        border: 1,
                        borderColor: "divider",
                        boxShadow: "none",
                        "&:before": { display: "none" },
                    }}
                >
                    <AccordionSummary expandIcon={<Icons.ExpandMore />}>
                        <Stack
                            direction="row"
                            spacing={2}
                            display="flex"
                            alignItems="center"
                        >
                            <Typography
                                variant="body2"
                                sx={{ fontWeight: 700 }}
                            >
                                <u>Configurar receta:</u>
                            </Typography>
                            <Typography variant="body2">
                                {selectedPreset?.name || "Custom"}
                                {isSelectedPresetModified
                                    ? " (modificado)"
                                    : ""}{" "}
                                · {schemeText}
                            </Typography>
                        </Stack>
                    </AccordionSummary>

                    <AccordionDetails>
                        <Stack spacing={1.5}>
                            <Paper
                                variant="outlined"
                                sx={{ px: 1.25, py: 0.9 }}
                            >
                                <Stack
                                    direction="row"
                                    spacing={2}
                                    flexWrap="wrap"
                                >
                                    <Typography variant="body2">
                                        <strong>Dadas:</strong> {safeDeals}
                                    </Typography>

                                    <Typography variant="body2">
                                        <strong>Paquetes:</strong> {safePackets}
                                    </Typography>

                                    <Typography variant="body2">
                                        <strong>Posiciones disponibles:</strong>{" "}
                                        {rows.length}
                                    </Typography>

                                    <Typography variant="body2">
                                        <strong>
                                            Posiciones no palindrómicas:
                                        </strong>{" "}
                                        {nonPalindromicCount}
                                    </Typography>

                                    <Typography variant="body2">
                                        <strong>Cartas asignadas:</strong>{" "}
                                        {countFilledCards(currentVisibleCards)}
                                    </Typography>
                                    <Typography variant="body2">
                                        <strong>Esquema:</strong> {schemeText}
                                    </Typography>
                                </Stack>
                            </Paper>

                            <Stack
                                direction={{ xs: "column", md: "row" }}
                                spacing={1.5}
                                alignItems={{ xs: "stretch", md: "center" }}
                            >
                                <TextField
                                    label="# de dadas"
                                    type="number"
                                    value={deals}
                                    onChange={handleDealsChange}
                                    onBlur={handleDealsBlur}
                                    inputProps={{ min: 1 }}
                                    size="small"
                                    sx={{ width: { xs: "100%", md: 150 } }}
                                />

                                <TextField
                                    label="# de paquetes"
                                    type="number"
                                    value={packets}
                                    onChange={handlePacketsChange}
                                    onBlur={handlePacketsBlur}
                                    inputProps={{ min: 1, max: 26 }}
                                    size="small"
                                    sx={{ width: { xs: "100%", md: 160 } }}
                                />

                                <FormControl
                                    size="small"
                                    sx={{ minWidth: 220, flex: 1 }}
                                >
                                    <InputLabel id="preset-select-label">
                                        Preset
                                    </InputLabel>
                                    <Select
                                        labelId="preset-select-label"
                                        label="Preset"
                                        value={selectedPresetId}
                                        onChange={handlePresetSelectChange}
                                    >
                                        <MenuItem value="custom">
                                            {`Custom (${countFilledCards(currentVisibleCards)} ${schemeText})`}
                                        </MenuItem>

                                        {BUILT_IN_PRESETS.length > 0 && (
                                            <MenuItem
                                                disabled
                                                value="__builtin-divider__"
                                            >
                                                Presets base
                                            </MenuItem>
                                        )}

                                        {BUILT_IN_PRESETS.map((preset) => (
                                            <MenuItem
                                                key={preset.id}
                                                value={preset.id}
                                            >
                                                {getPresetLabel(preset)}
                                                {preset.id ===
                                                    selectedPresetId &&
                                                isSelectedPresetModified
                                                    ? " · Modificado"
                                                    : ""}
                                            </MenuItem>
                                        ))}

                                        {normalUserPresets.length > 0 && (
                                            <MenuItem
                                                disabled
                                                value="__user-divider__"
                                            >
                                                Guardados
                                            </MenuItem>
                                        )}

                                        {normalUserPresets.map((preset) => (
                                            <MenuItem
                                                key={preset.id}
                                                value={preset.id}
                                            >
                                                {getPresetLabel(preset)}
                                                {preset.id ===
                                                    selectedPresetId &&
                                                isSelectedPresetModified
                                                    ? " · Modificado"
                                                    : ""}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <Stack
                                    direction="row"
                                    spacing={0.5}
                                    alignItems="center"
                                >
                                    <Tooltip
                                        title="Guardar preset actual"
                                        arrow
                                    >
                                        <IconButton
                                            onClick={() => {
                                                if (
                                                    selectedPreset &&
                                                    !selectedPreset.isBuiltIn
                                                ) {
                                                    setIsSavePresetDialogOpen(
                                                        true,
                                                    );
                                                    return;
                                                }

                                                handleSavePreset();
                                            }}
                                        >
                                            <Icons.Save />
                                        </IconButton>
                                    </Tooltip>

                                    <Tooltip
                                        title="Eliminar preset seleccionado"
                                        arrow
                                    >
                                        <span>
                                            <IconButton
                                                disabled={!canDeletePreset}
                                                onClick={() =>
                                                    setConfirmationDialog(
                                                        "deletePreset",
                                                    )
                                                }
                                            >
                                                <Icons.Delete />
                                            </IconButton>
                                        </span>
                                    </Tooltip>

                                    <Tooltip title="Descargar guardados" arrow>
                                        <span>
                                            <IconButton
                                                onClick={handleExportPresets}
                                                disabled={
                                                    !hasCurrentVisibleCards ||
                                                    isExporting ||
                                                    isImporting
                                                }
                                            >
                                                {isExporting ? (
                                                    <CircularProgress
                                                        size={20}
                                                    />
                                                ) : (
                                                    <Icons.Download />
                                                )}
                                            </IconButton>
                                        </span>
                                    </Tooltip>

                                    <Tooltip title="Cargar archivo" arrow>
                                        <span>
                                            <IconButton
                                                onClick={handleOpenImportDialog}
                                                disabled={
                                                    isImporting || isExporting
                                                }
                                            >
                                                {isImporting ? (
                                                    <CircularProgress
                                                        size={20}
                                                    />
                                                ) : (
                                                    <Icons.Upload />
                                                )}
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    <Tooltip title="Deshacer (Ctrl+Z)" arrow>
                                        <span>
                                            <IconButton
                                                disabled={!canUndo}
                                                onClick={handleUndo}
                                            >
                                                <Icons.Undo />
                                            </IconButton>
                                        </span>
                                    </Tooltip>

                                    <Tooltip title="Rehacer (Ctrl+Y)" arrow>
                                        <span>
                                            <IconButton
                                                disabled={!canRedo}
                                                onClick={handleRedo}
                                            >
                                                <Icons.Redo />
                                            </IconButton>
                                        </span>
                                    </Tooltip>

                                    <Tooltip title="Editar nombre" arrow>
                                        <span>
                                            <IconButton
                                                disabled={!canRenamePreset}
                                                onClick={
                                                    handleOpenRenamePresetDialog
                                                }
                                            >
                                                <Icons.Edit />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </Stack>
                            </Stack>

                            <Stack direction="row">
                                <TextField
                                    label="Descripción"
                                    value={descriptionDraft}
                                    onChange={handleDescriptionChange}
                                    onBlur={commitDescription}
                                    multiline
                                    fullWidth
                                    minRows={2}
                                    maxRows={
                                        descriptionViewMode === "compact"
                                            ? 2
                                            : undefined
                                    }
                                    placeholder="Escribe una descripción para este preset..."
                                    size="small"
                                    sx={{
                                        "& .MuiInputBase-root": {
                                            alignItems:
                                                descriptionViewMode ===
                                                "compact"
                                                    ? "flex-start"
                                                    : "stretch",
                                        },
                                    }}
                                />

                                <ToggleButtonGroup
                                    exclusive
                                    size="small"
                                    value={descriptionViewMode}
                                    onChange={handleDescriptionViewModeChange}
                                >
                                    {descriptionViewMode === "compact" ? (
                                        <ToggleButton
                                            value="full"
                                            sx={{ textTransform: "none" }}
                                        >
                                            <Icons.UnfoldMore />
                                        </ToggleButton>
                                    ) : (
                                        <ToggleButton
                                            value="compact"
                                            sx={{ textTransform: "none" }}
                                        >
                                            <Icons.UnfoldLess />
                                        </ToggleButton>
                                    )}
                                </ToggleButtonGroup>
                            </Stack>

                            {showWarning && (
                                <Alert severity="warning" sx={{ py: 0.5 }}>
                                    Este componente usa letras A-Z. El máximo
                                    recomendado para paquetes es 26.
                                </Alert>
                            )}

                            <Paper variant="outlined" sx={{ px: 1.25, py: 1 }}>
                                <RadicalPictogramSelector
                                    packets={safePackets}
                                    pictograms={currentPictograms}
                                    onPictogramChange={handlePictogramChange}
                                    onLayoutSelect={handlePictogramLayoutSelect}
                                />
                            </Paper>
                        </Stack>
                    </AccordionDetails>
                </Accordion>

                <Paper variant="outlined" sx={{ p: 0.75, overflowX: "auto" }}>
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={2}
                        sx={{
                            justifyContent: "flex-start",
                            alignItems: "center",
                        }}
                    >
                        <ToggleButtonGroup
                            exclusive
                            size="small"
                            value={deckOrientation}
                            onChange={handleDeckOrientationChange}
                        >
                            <ToggleButton
                                value="cara"
                                sx={{
                                    textTransform: "none",
                                    "&.Mui-selected": {
                                        fontWeight: "bold",
                                    },
                                }}
                            >
                                Top cara
                            </ToggleButton>
                            <ToggleButton
                                value="dorso"
                                sx={{
                                    textTransform: "none",
                                    "&.Mui-selected": {
                                        fontWeight: "bold",
                                    },
                                }}
                            >
                                Top dorso
                            </ToggleButton>
                        </ToggleButtonGroup>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                                whiteSpace: "pre-line",
                                lineHeight: 1,
                                display: "block",
                                mb: 1,
                            }}
                        >
                            {`La posición (#) será la obtenida tras repartir ${safeDeals} dadas en ${safePackets} paquetes y dejar la baraja `}
                            <span
                                style={{ fontWeight: 700 }}
                            >{`${deckOrientationMeta.label}.`}</span>
                            {"\n"}
                            {`Las cartas se repartirán cara abajo y ${deckOrientationMeta.finalActionText}`}
                        </Typography>
                        <Stack
                            direction="row"
                            spacing={0.5}
                            alignItems="center"
                        >
                            <Tooltip title="Descargar receta" arrow>
                                <span>
                                    <IconButton
                                        onClick={handleOpenTableExportDialog}
                                        disabled={isTableExporting}
                                    >
                                        {isTableExporting ? (
                                            <CircularProgress size={18} />
                                        ) : (
                                            <Icons.Download fontSize="small" />
                                        )}
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip title="Limpiar cartas asignadas" arrow>
                                <span>
                                    <IconButton
                                        disabled={!hasCurrentVisibleCards}
                                        onClick={() =>
                                            setConfirmationDialog("clearCards")
                                        }
                                    >
                                        <Icons.Backspace />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip
                                title="Voltear receta completa, incluyendo huecos"
                                arrow
                            >
                                <span>
                                    <IconButton
                                        onClick={handleFlipCurrentCards}
                                        disabled={!hasCurrentVisibleCards}
                                    >
                                        <Icons.FlipRecipe />
                                    </IconButton>
                                </span>
                            </Tooltip>

                            <Tooltip
                                title="Voltear solo las cartas rellenas"
                                arrow
                            >
                                <span>
                                    <IconButton
                                        onClick={handleFlipFilledCards}
                                        disabled={!hasCurrentVisibleCards}
                                    >
                                        <Icons.FlipFilled />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            {safeDeals === 2 && (
                                <Tooltip
                                    title={
                                        cardsViewMode === "grid"
                                            ? "Mostrar en dos tablas"
                                            : "Mostrar como casillero"
                                    }
                                    arrow
                                >
                                    <IconButton
                                        size="small"
                                        onClick={() =>
                                            setCardsViewMode((prev) =>
                                                prev === "grid"
                                                    ? "tables"
                                                    : "grid",
                                            )
                                        }
                                    >
                                        {cardsViewMode === "grid" ? (
                                            <Icons.Tables />
                                        ) : (
                                            <Icons.Grid />
                                        )}
                                    </IconButton>
                                </Tooltip>
                            )}
                            <Tooltip
                                title={
                                    !isFocusMode && !hasCurrentVisibleCards
                                        ? "Asigna al menos una carta"
                                        : isFocusMode
                                          ? "Volver a la tabla"
                                          : "Vista de concentración"
                                }
                                arrow
                            >
                                <span>
                                    <Button
                                        size="small"
                                        variant={
                                            isFocusMode
                                                ? "contained"
                                                : "outlined"
                                        }
                                        disabled={
                                            !isFocusMode &&
                                            !hasCurrentVisibleCards
                                        }
                                        aria-pressed={isFocusMode}
                                        onClick={(event) => {
                                            event.currentTarget.blur();
                                            setIsFocusMode(
                                                (previous) => !previous,
                                            );
                                        }}
                                        sx={{ textTransform: "none" }}
                                    >
                                        {isFocusMode
                                            ? "Volver a tabla"
                                            : "Concentración"}
                                    </Button>
                                </span>
                            </Tooltip>
                            <Tooltip title="Ver atajos de cartas" arrow>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() =>
                                        setIsCardAliasesDialogOpen(true)
                                    }
                                    sx={{ textTransform: "none" }}
                                >
                                    Atajos
                                </Button>
                            </Tooltip>
                        </Stack>
                    </Stack>

                    {/* <Typography
                        variant="body2"
                        sx={{ fontWeight: 700, mb: 0.75 }}
                    >
                        {deckOrientationMeta.topLabel}
                    </Typography> */}
                    <Paper
                        variant="outlined"
                        sx={{
                            p: 0.75,
                            overflowX: isFocusMode ? "hidden" : "auto",
                        }}
                    >
                        {isFocusMode ? (
                            <RadicalFocusView
                                displayRows={displayRows}
                                allRows={rows}
                                cardsByCode={cardsByCode}
                                packets={safePackets}
                                pictograms={currentPictograms}
                                deckOrientation={deckOrientation}
                                onClose={() => setIsFocusMode(false)}
                            />
                        ) : safeDeals === 2 && cardsViewMode === "grid" ? (
                            <TwoDealGrid
                                rows={rows}
                                cardsByCode={cardsByCode}
                                onCardChange={handleCardChange}
                                cardInputRefs={cardInputRefs}
                                packets={safePackets}
                                pictograms={currentPictograms}
                                deckOrientation={deckOrientation}
                                highlightedCodes={highlightedCodes}
                            />
                        ) : (
                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: {
                                        xs: "1fr",
                                        md: "max-content max-content",
                                    },
                                    gap: 4,
                                    justifyContent: {
                                        xs: "stretch",
                                        md: "center",
                                    },
                                    alignItems: "start",
                                }}
                            >
                                <CompactTable
                                    rows={leftRows}
                                    allRows={rows}
                                    cardsByCode={cardsByCode}
                                    onCardChange={handleCardChange}
                                    packets={safePackets}
                                    pictograms={currentPictograms}
                                    deckOrientation={deckOrientation}
                                    onCardMove={handleCardMove}
                                    onCardKeyDown={handleCardKeyDown}
                                    cardInputRefs={cardInputRefs}
                                    highlightedCodes={highlightedCodes}
                                />

                                <CompactTable
                                    rows={rightRows}
                                    allRows={rows}
                                    cardsByCode={cardsByCode}
                                    onCardChange={handleCardChange}
                                    packets={safePackets}
                                    pictograms={currentPictograms}
                                    deckOrientation={deckOrientation}
                                    onCardMove={handleCardMove}
                                    onCardKeyDown={handleCardKeyDown}
                                    cardInputRefs={cardInputRefs}
                                    highlightedCodes={highlightedCodes}
                                />
                            </Box>
                        )}
                    </Paper>

                    {/* <Typography
                        variant="body2"
                        sx={{ fontWeight: 700, mt: 0.75 }}
                    >
                        {deckOrientationMeta.bottomLabel}
                    </Typography> */}
                </Paper>
                {!isFocusMode && (
                    <RadicalSimulation
                        rows={rows}
                        cardsByCode={cardsByCode}
                        packets={safePackets}
                        deals={safeDeals}
                        pictograms={currentPictograms}
                        deckOrientation={deckOrientation}
                        onTableHighlightChange={
                            handleSimulationTableHighlightChange
                        }
                    />
                )}
            </Stack>
            <RadicalMoveDialog
                open={isRadicalMoveDialogOpen}
                onClose={() => setIsRadicalMoveDialogOpen(false)}
                rows={rows}
                cardsByCode={cardsByCode}
                currentPictograms={currentPictograms}
                safePackets={safePackets}
            />
            <RadicalSymbolSwapDialog
                open={isSymbolSwapDialogOpen}
                onClose={() => setIsSymbolSwapDialogOpen(false)}
                rows={rows}
                cardsByCode={cardsByCode}
                safePackets={safePackets}
                safeDeals={safeDeals}
                currentPictograms={currentPictograms}
                deckOrientation={deckOrientation}
                selectedPresetName={selectedPreset?.name}
                description={descriptionDraft}
                handlingPresets={selectedPreset?.handlingPresets || []}
                onHandlingPresetsChange={handleHandlingPresetsChange}
            />
            <DialogExport
                open={isTableExportDialogOpen}
                onClose={handleCloseTableExportDialog}
                onExportingChange={setIsTableExporting}
                rows={rows}
                displayRows={displayRows}
                cardsByCode={cardsByCode}
                deckOrientation={deckOrientation}
                safeDeals={safeDeals}
                safePackets={safePackets}
                currentPictograms={currentPictograms}
                selectedPresetName={selectedPreset?.name}
                showFeedback={showFeedback}
            />
            <RadicalAdvancedMode
                open={isAdvancedModeOpen}
                onClose={() => {
                    setIsAdvancedModeOpen(false);
                    setAdvancedSearchPresetId("");
                }}
                initialPresetId={advancedSearchPresetId}
                initialRecipeValues={displayRows.map(
                    (row) => cardsByCode[row.finalCode] || "",
                )}
                defaultDeals={safeDeals}
                defaultPackets={safePackets}
                deckOrientation={deckOrientation}
                pictogramsByPackets={pictogramsByPackets}
                presets={advancedUserPresets}
                onSavePreset={handleSaveAdvancedPreset}
                onDeletePreset={handleDeleteAdvancedPreset}
                onRenamePreset={handleRenameAdvancedPreset}
            />
            <RadicalSearchDialog
                open={isSearchDialogOpen}
                onClose={() => setIsSearchDialogOpen(false)}
                presets={[...allPresets, ...advancedUserPresets]}
                pictogramsByPackets={pictogramsByPackets}
                onSelect={(preset) => {
                    setIsSearchDialogOpen(false);

                    if (preset.recipeType === "advanced") {
                        setAdvancedSearchPresetId(preset.id);
                        setIsAdvancedModeOpen(true);
                    } else {
                        handlePresetSelectChange({
                            target: { value: preset.id },
                        });
                    }
                }}
            />
            <Snackbar
                open={feedback.open}
                autoHideDuration={5000}
                onClose={handleCloseFeedback}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={handleCloseFeedback}
                    severity={feedback.severity}
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {feedback.message}
                </Alert>
            </Snackbar>
            {/* ATAJOS CARTAS */}
            <Dialog
                open={isCardAliasesDialogOpen}
                onClose={() => setIsCardAliasesDialogOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Atajos de cartas</DialogTitle>

                <DialogContent dividers>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        Escribe los siguientes textos entre ":" para mostrar el
                        símbolo en la tabla.
                    </Typography>
                    <Stack spacing={1.5}>
                        {CARD_SUIT_ALIAS_GROUPS.map((group) => (
                            <Box key={group.symbol}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 700,
                                        mb: 0.5,
                                        color: group.color,
                                    }}
                                >
                                    {group.symbol}
                                </Typography>

                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                        fontFamily: "monospace",
                                        whiteSpace: "normal",
                                    }}
                                >
                                    {group.aliases.join("  ")}
                                </Typography>
                            </Box>
                        ))}
                    </Stack>
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setIsCardAliasesDialogOpen(false)}>
                        Cerrar
                    </Button>
                </DialogActions>
            </Dialog>
            <RadicalSavePresetDialog
                open={isSavePresetDialogOpen}
                onClose={() => setIsSavePresetDialogOpen(false)}
                presetName={selectedPreset?.name}
                onCreate={() => handleSavePreset()}
                onOverwrite={() => handleSavePreset(true)}
            />

            <RadicalConfirmDialog
                open={Boolean(confirmationDialog)}
                onClose={() => setConfirmationDialog("")}
                title={
                    confirmationDialog === "clearCards"
                        ? "Limpiar cartas"
                        : "Borrar receta"
                }
                description={
                    confirmationDialog === "clearCards"
                        ? "Se eliminarán todas las cartas asignadas de la receta actual. Esta acción no se puede deshacer."
                        : `Se borrará la receta “${
                              selectedPreset?.name || "seleccionada"
                          }”. Esta acción no se puede deshacer.`
                }
                confirmText={
                    confirmationDialog === "clearCards" ? "Limpiar" : "Borrar"
                }
                onConfirm={() => {
                    if (confirmationDialog === "clearCards") {
                        handleClearCurrentCards();
                    } else {
                        handleDeletePreset();
                    }

                    setConfirmationDialog("");
                }}
            />

            <RadicalUnsavedChangesDialog
                open={Boolean(pendingPresetId)}
                onClose={() => setPendingPresetId("")}
                currentPresetName={selectedPreset?.name}
                nextPresetName={
                    allPresets.find((preset) => preset.id === pendingPresetId)
                        ?.name || "otro preset"
                }
                onDiscard={() => {
                    const presetId = pendingPresetId;

                    setPendingPresetId("");
                    loadPreset(presetId);
                }}
            />

            <RadicalRenamePresetDialog
                open={isRenamePresetDialogOpen}
                onClose={() => setIsRenamePresetDialogOpen(false)}
                value={presetNameDraft}
                onChange={(event) => setPresetNameDraft(event.target.value)}
                onSave={handleRenamePreset}
            />
            {/* HERRAMIENTAS / SPEED DIAL */}
            <SpeedDial
                ariaLabel="Herramientas de juego"
                icon={<SpeedDialIcon />}
                direction="up"
                sx={{
                    position: "fixed",
                    right: 24,
                    bottom: 24,
                    zIndex: 1300,
                    "& .MuiSpeedDial-actions": {
                        gap: 0.75,
                    },
                }}
            >
                {playToolActions.map((action) => (
                    <SpeedDialAction
                        key={action.name}
                        icon={
                            <Typography
                                component="span"
                                sx={{
                                    fontSize: 20,
                                    lineHeight: 1,
                                }}
                            >
                                {action.icon}
                            </Typography>
                        }
                        onClick={action.onClick}
                        slotProps={{
                            tooltip: {
                                title: action.name,
                                open: true,
                                placement: "left",
                            },
                            staticTooltipLabel: {
                                sx: {
                                    whiteSpace: "nowrap",
                                    maxWidth: 220,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    px: 1.25,
                                    py: 0.75,
                                    borderRadius: 1.5,
                                    fontSize: 13,
                                    fontWeight: 500,
                                },
                            },
                        }}
                    />
                ))}

                {!authUser && (
                    <SpeedDialAction
                        icon={<GoogleIcon />}
                        onClick={() => {
                            if (busy) return;

                            loginAllInOne().catch(() => {
                                showFeedback(
                                    "No se pudo iniciar sesión con Google.",
                                    "error",
                                );
                            });
                        }}
                        slotProps={{
                            tooltip: {
                                title: busy
                                    ? "Abriendo Google…"
                                    : "Iniciar sesión con Google",
                                open: true,
                                placement: "left",
                            },
                            staticTooltipLabel: {
                                sx: {
                                    whiteSpace: "nowrap",
                                    maxWidth: 220,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    px: 1.25,
                                    py: 0.75,
                                    borderRadius: 1.5,
                                    fontSize: 13,
                                    fontWeight: 500,
                                },
                            },
                        }}
                    />
                )}

                {authUser && (
                    <SpeedDialAction
                        icon={
                            <Avatar
                                src={
                                    authUser.user_metadata?.avatar_url ||
                                    authUser.user_metadata?.picture
                                }
                                alt={
                                    authUser.user_metadata?.full_name ||
                                    authUser.email
                                }
                                sx={{
                                    width: 32,
                                    height: 32,
                                }}
                            />
                        }
                        slotProps={{
                            tooltip: {
                                title:
                                    authUser.email ||
                                    authUser.user_metadata?.full_name ||
                                    "Cuenta de Google",
                                open: true,
                                placement: "left",
                            },
                            staticTooltipLabel: {
                                sx: {
                                    whiteSpace: "nowrap",
                                    maxWidth: 220,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    px: 1.25,
                                    py: 0.75,
                                    borderRadius: 1.5,
                                    fontSize: 13,
                                    fontWeight: 500,
                                },
                            },
                        }}
                    />
                )}

                {authUser && (
                    <SpeedDialAction
                        icon={<LogoutIcon />}
                        onClick={() => {
                            if (busy) return;

                            logout()
                                .then(() => {
                                    showFeedback("Sesión cerrada.");
                                })
                                .catch(() => {
                                    showFeedback(
                                        "No se pudo cerrar la sesión.",
                                        "error",
                                    );
                                });
                        }}
                        slotProps={{
                            tooltip: {
                                title: busy
                                    ? "Cerrando sesión…"
                                    : "Cerrar sesión",
                                open: true,
                                placement: "left",
                            },
                            staticTooltipLabel: {
                                sx: {
                                    whiteSpace: "nowrap",
                                    maxWidth: 220,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    px: 1.25,
                                    py: 0.75,
                                    borderRadius: 1.5,
                                    fontSize: 13,
                                    fontWeight: 500,
                                },
                            },
                        }}
                    />
                )}
            </SpeedDial>
        </Box>
    );
};

export default RadicalScreen;
